import React, { useEffect, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import InfiniteScroll from 'react-infinite-scroll-component';
import SearchSuggestions from './SearchSuggestions';
import ManualBreadcrumbs from '@components/listing/ManualBreadcrumbs';
import { useInView } from 'react-intersection-observer';
import { SearchActionTypes } from '@state/features/common.types';
import einsteinutils from '@utils/einsteinutils';
import ProductTilesListContext from '@stone/scripts/contexts/ProductTilesListContext';
import ProductFilters from '@stone/components/listing/ProductFilters';
import ProductTile from '@stone/components/product/ProductTile';
import SearchField from './SearchField';
import SearchNoResults from './SearchNoResults';
import analytics from '@stone/utils/analytics';
import classNames from 'classnames';
import SearchCloseBtn from './SearchCloseBtn';
const SearchContainer = (props) => {
    const { t } = useTranslation();
    // const noResultsEditorial = JSON.parse(props.noResultsEditorial);
    // * ANCHOR - REDUX ----------------------------------------------------------------------------------------------------------------------------------------
    const dispatch = useDispatch();
    const { resultsType, inputSearch, results, progress, count, productSort } = useSelector((state) => state.search);
    const isLoading = useSelector((state) => state.loading).requests.includes('SEARCH');
    const { apiErrors } = useSelector((state) => state.errors);
    // * ANCHOR - USE REF ----------------------------------------------------------------------------------------------------------------------------------------
    const firstRender = useRef(true);
    const firstSend = useRef(false);
    useEffect(() => {
        if (!isLoading && !firstRender.current) {
            if (!firstSend.current) {
                analytics.searchResult(inputSearch, count);
                firstSend.current = true;
            }
        }
        else if (isLoading) {
            firstRender.current = false;
        }
    }, [isLoading]);
    const { ref, inView } = useInView();
    // * ANCHOR - USE MEMO ----------------------------------------------------------------------------------------------------------------------------------------
    const hasMore = useMemo(() => {
        return progress < count;
    }, [progress, count]);
    const loadingError = useMemo(() => {
        return apiErrors.find((error) => {
            return error.metadata.location === 'handleSearchFetch';
        });
    }, [apiErrors]);
    // * ANCHOR - FUNCTIONS ----------------------------------------------------------------------------------------------------------------------------------------
    const showAccessibleLoadMore = () => {
        if (results.length && hasMore) {
            return (React.createElement("div", { className: "container visible-on-focus" },
                React.createElement("h3", { className: "plp-loading" },
                    " ",
                    t('plp.moreproducts')),
                React.createElement("button", { className: "button button--secondary", onClick: fetchProducts }, t('plp.loadmore'))));
        }
        return React.createElement(React.Fragment, null);
    };
    const fetchProducts = () => {
        dispatch({
            type: SearchActionTypes.FETCH_SEARCH,
            payload: {
                start: progress,
                resultsType: 'PAGE',
            },
        });
    };
    const loader = () => {
        return (!loadingError && (React.createElement("div", { className: "container" },
            React.createElement("span", { className: "plp-loading", "aria-live": "polite" }, t('plp.loading')))));
    };
    const error = () => {
        if (loadingError && !isLoading) {
            return (React.createElement("div", { className: "container" },
                React.createElement("span", { className: "plp-error", "aria-live": "polite" }, t('plp.error'))));
        }
        return React.createElement(React.Fragment, null);
    };
    const searchContext = useMemo(() => {
        let context = {
            analyticsData: {
                item_list_name: 'search results',
            },
        };
        return context;
    }, []);
    const resultsNumber = useMemo(() => {
        return React.createElement(Trans, { i18nKey: "search.results.messagem", values: { count: count }, components: [, React.createElement("strong", { key: "text" })] });
    }, [count]);
    return (React.createElement(React.Fragment, null,
        React.createElement(SearchField, { searchData: props.searchData }),
        React.createElement("div", { className: "search-observer-target", ref: ref }),
        React.createElement("div", { className: classNames('search-sticky-header container--fluid', { scrolled: !inView }) },
            React.createElement("span", { className: "font__title" }, t('search.dialog.inputlabel')),
            React.createElement(SearchCloseBtn, { handleClose: props.handleClose })),
        !resultsType ? (React.createElement(SearchSuggestions, { searchData: props.searchData })) : (!firstRender.current && (React.createElement(React.Fragment, null,
            count > 0 ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "search-page-header" },
                    React.createElement("div", { className: "container" },
                        React.createElement("h2", { className: "search-page-header--title" }, resultsNumber))),
                React.createElement("div", { className: classNames('search-page-container', { scrolled: !inView }), id: "searchResults" },
                    React.createElement(ProductFilters, { fromSearch: true }),
                    React.createElement("div", { className: "container" },
                        React.createElement(ProductTilesListContext.Provider, { value: searchContext },
                            React.createElement(InfiniteScroll, { dataLength: results.length, next: fetchProducts, hasMore: hasMore, loader: loader(), scrollableTarget: "infinite-scroll-wrapper" },
                                React.createElement("ul", { className: "grid-container" }, results.map((product, i) => (React.createElement("li", { key: i, className: "product" },
                                    React.createElement(ProductTile, { product: product, index: i, type: 'SEARCH' }),
                                    einsteinutils.trackProductInSearch(product.id, inputSearch, productSort === null || productSort === void 0 ? void 0 : productSort.ruleId) && React.createElement(React.Fragment, null))))),
                                showAccessibleLoadMore(),
                                error())))))) : (React.createElement(SearchNoResults, { inputSearch: inputSearch, resultsNumber: resultsNumber })),
            React.createElement(ManualBreadcrumbs, { components: [
                    {
                        label: t('label.sitename'),
                        url: window.homeurl,
                    },
                    {
                        label: t('search.noresults.currentpage', { text: inputSearch }),
                    },
                ] }))))));
};
export default SearchContainer;
